import Box from "@mui/material/Box";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { useTranslation } from "react-i18next";
import Loader from "layout/Loader";
import BackendPaginationTable from "common/Table/TableWithPagination";
import { useNavigate, useParams } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Client } from "assets/SideBarIcons/customer.svg";
import { useGetCustomerIntegrationService } from "subModule/src/services/client/useClient";
import ClientTabs from "./ClientTabs";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  CustomerIntegrations,
  GetClientByIdResponse,
} from "subModule/src/services/client/interface";
import { Grid, Typography } from "@mui/material";
import { ClientProfileService } from "subModule/src/core/services";
import { Wrapper } from "subModule/src/type";
import FormDialog from "common/Modal/Modal";
import { useFrozenClientTabBreadcrumbs } from "Helpers/useFrozenClientBreadcrumbs";

const resolveCustomerBusinessId = (source: any): number | undefined => {
  const candidates = [
    source?.businessId,
    source?.business?.id,
    source?.account?.businessId,
    source?.account?.business?.id,
  ];
  for (const value of candidates) {
    const id = Number(value);
    if (Number.isFinite(id) && id > 0) return id;
  }
  return undefined;
};

export default function ClientIntegrationsListing() {
  const navigate = useNavigate();
  const params = useParams();
  const customerId = Number(params?.id);
  const [businessId, setBusinessId] = useState<number>(0);
  const [resolvingBusinessId, setResolvingBusinessId] = useState(true);
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [row, setRow] = useState<CustomerIntegrations>({});
  const [loading, setLoading] = useState<boolean>(false);
  const {
    data: tableData,
    isRefetching,
    refetch,
  } = useGetCustomerIntegrationService(businessId);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const breadcrumbItems = useFrozenClientTabBreadcrumbs("integrations");

  const integrationFormBase = `/client/${params?.id}/account/${params?.accountId}/integration`;

  const listingData = useMemo(() => {
    const payload: any = tableData;
    const rows = Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload)
        ? payload
        : [];
    return rows.filter((item: CustomerIntegrations) => {
      if (item?.customerId == null) return true;
      return Number(item.customerId) === customerId;
    });
  }, [tableData, customerId]);

  useEffect(() => {
    if (!customerId) {
      setResolvingBusinessId(false);
      return;
    }
    setResolvingBusinessId(true);
    ClientProfileService.getClientByID(customerId, "clients")
      .then((res: GetClientByIdResponse) => {
        if (res?.success) {
          const id = resolveCustomerBusinessId(res?.data);
          setBusinessId(id || 0);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res?.message || "Something went wrong"} />,
            { variant: "error" },
          );
          setBusinessId(0);
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
        setBusinessId(0);
      })
      .finally(() => setResolvingBusinessId(false));
  }, [customerId, enqueueSnackbar]);

  const fetchData = useCallback(() => {
    if (!businessId) return;
    refetch().then((res: any) => {
      const payload = res?.data;
      const ok =
        payload?.success === true ||
        Array.isArray(payload?.data) ||
        Array.isArray(payload);
      if (!ok)
        enqueueSnackbar(
          <AlertMessages text={payload?.message || "Something went wrong"} />,
          { variant: "error" },
        );
    });
  }, [businessId, enqueueSnackbar, refetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onEdit = (row: any) =>
    navigate(
      `${integrationFormBase}/${row.id}/invoicingPreference/${params?.invoicingPreference}`,
    );
  const showRowView = (row: any) =>
    navigate(
      `${integrationFormBase}/${row.id}/invoicingPreference/${params?.invoicingPreference}`,
    );
  const onDelete = (row: any) => {
    setDeleteModal(true);
    setRow(row);
  };
  const actionslist = [
    { label: "edit", onClick: onEdit, icon: <EditIcon /> },
    { label: "delete", onClick: onDelete, icon: <DeleteIcon /> },
  ];

  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/clients")}
        heading={t("customer") + " (" + t("company") + ")"}
        icon={<Client height={28} width={28} />}
        btnText={"back"}
        btnType={"back"}
        showCreateBtn={true}
        createBtnText={"add"}
        onCreateClick={() =>
          navigate(
            `${integrationFormBase}/add/invoicingPreference/${params?.invoicingPreference}`,
          )
        }
        breadcrumbItems={breadcrumbItems}
      />
      <Box className="flex">
        <ClientTabs params={params} selectedIndex={8} />
        <Box className="w-[100%] mt-[10px]">
          {resolvingBusinessId || isRefetching || loading ? (
            <Loader />
          ) : (
            <div id="mainListingTable" className="mt-[10px]">
              <BackendPaginationTable
                columns={[
                  { id: "title", label: "title", numeric: false, width: "50%" },
                  {
                    id: "integrationType",
                    label: "type",
                    numeric: false,
                    width: "35%",
                  },
                  {
                    id: "actions",
                    label: "actions",
                    numeric: false,
                    width: "15%",
                  },
                ]}
                data={listingData}
                pageNo={1}
                pageSize={20}
                tableData={tableData || {}}
                showCheckbox={false}
                showActions={true}
                enableSorting={true}
                showHeader={false}
                showSearch={true}
                showFilter={true}
                showCreateButton={true}
                actions={actionslist}
                handleChangePage={() => {}}
                handleChangeRows={() => {}}
                rowClick={true}
                showRowView={showRowView}
                loadingSearch={false}
                headerComponent={<></>}
                hidePagination={true}
              />
            </div>
          )}
        </Box>
      </Box>
      {openDeleteModal && (
        <FormDialog
          open={openDeleteModal}
          onClose={() => setDeleteModal(false)}
          title={t("delete") + " " + t("integration")}
          content={
            <Grid container>
              <Grid item xs={12}>
                <Typography>{t("Are you sure you want to delete?")}</Typography>
              </Grid>
            </Grid>
          }
          saveFunction={() => {
            setDeleteModal(false);
            setLoading(true);
            ClientProfileService.deleteCustomerIntegration((row as any).id)
              .then((response: Wrapper) => {
                if (response.success) {
                  refetch();
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={response.status}
                      text={t("deleted") + " " + t("successfully")}
                    />,
                    { variant: "success" },
                  );
                } else {
                  enqueueSnackbar(
                    <AlertMessages statusCode={response.status} />,
                    { variant: "error" },
                  );
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={err?.data?.message || err || "Something went wrong"}
                  />,
                  { variant: "error" },
                );
              })
              .finally(() => setLoading(false));
          }}
          submitButtonText="Delete"
        />
      )}
    </Box>
  );
}
